/**
 * PhotoGallery Component
 * Displays and manages photos for jobs, estimates, or clients
 */

import { useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Image, 
  Upload, 
  X, 
  Trash2, 
  ZoomIn,
  Camera,
  Loader2,
  Download,
  Layers
} from 'lucide-react';
import { cn } from '@/lib/utils';

const PhotoGallery = ({ 
  entityType, // 'job', 'estimate', or 'client'
  entityId,
  photos = [],
  onPhotosChange,
  readOnly = false,
  maxPhotos = 20
}) => {
  const { organization, user } = useAuth();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadType, setUploadType] = useState('general');
  const [showCompareView, setShowCompareView] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type', { description: 'Please upload JPEG, PNG, WEBP, or HEIC' });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large', { description: 'Max file size is 10MB' });
      return;
    }

    setPendingFile(file);
    setShowUploadDialog(true);
  };

  const handleUpload = async () => {
    if (!pendingFile || !organization?.id) return;

    setIsUploading(true);
    try {
      const response = await apiHelpers.uploadPhoto(
        organization.id,
        entityType,
        entityId,
        pendingFile,
        user?.id,
        user?.name,
        uploadCaption,
        uploadType
      );

      const newPhoto = {
        id: response.data.id,
        url: response.data.url,
        thumbnail_url: response.data.thumbnail_url,
        drive_file_id: response.data.drive_file_id,
        caption: uploadCaption,
        photo_type: uploadType
      };

      onPhotosChange?.([...photos, newPhoto]);
      toast.success('Photo uploaded');
      
      // Reset
      setPendingFile(null);
      setUploadCaption('');
      setUploadType('general');
      setShowUploadDialog(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    const confirmed = await showConfirm({
      title: 'Delete Photo?',
      description: 'This photo will be permanently removed.',
      variant: 'danger',
      confirmText: 'Delete',
      destructive: true
    });
    if (!confirmed) return;

    try {
      await apiHelpers.deletePhoto(organization.id, photoId, user?.id);
      onPhotosChange?.(photos.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
      setSelectedPhoto(null);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete photo');
    }
  };

  const getPhotoUrl = (photo, useThumbnail = false) => {
    // For Drive photos, use thumbnail for grid display
    if (useThumbnail && photo.thumbnail_url) {
      return photo.thumbnail_url;
    }
    if (photo.url?.startsWith('http')) return photo.url;
    return `${API_BASE}${photo.url}`;
  };

  const photoTypeLabels = {
    before: 'Before',
    after: 'After',
    progress: 'In Progress',
    general: 'General'
  };

  const photoTypeColors = {
    before: 'bg-blue-100 text-blue-700',
    after: 'bg-green-100 text-green-700',
    progress: 'bg-amber-100 text-amber-700',
    general: 'bg-gray-100 text-gray-700'
  };

  // Get before/after comparison pairs
  const getComparisonPairs = () => {
    const beforePhotos = photos.filter(p => p.photo_type === 'before');
    const afterPhotos = photos.filter(p => p.photo_type === 'after');
    
    const pairs = [];
    const maxPairs = Math.max(beforePhotos.length, afterPhotos.length);
    
    for (let i = 0; i < maxPairs; i++) {
      pairs.push({
        before: beforePhotos[i] || null,
        after: afterPhotos[i] || null,
      });
    }
    
    return pairs;
  };

  const hasBeforeAfterPhotos = photos.some(p => p.photo_type === 'before' || p.photo_type === 'after');

  // Download photo
  const downloadPhoto = (photo) => {
    const url = getPhotoUrl(photo);
    const link = document.createElement('a');
    link.href = url;
    link.download = `photo_${photo.id}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download all photos
  const downloadAllPhotos = () => {
    photos.forEach((photo, i) => {
      setTimeout(() => downloadPhoto(photo), i * 300);
    });
    toast.success('Downloading photos...');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-medium">Photos</h3>
          <span className="text-sm text-muted-foreground">({photos.length})</span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Compare toggle - only show if there are before/after photos */}
          {hasBeforeAfterPhotos && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCompareView(!showCompareView)}
              className={showCompareView ? 'bg-primary/10 border-primary' : ''}
              data-testid="compare-photos-btn"
            >
              <Layers className="w-4 h-4 mr-2" />
              Compare
            </Button>
          )}
          
          {/* Download all */}
          {photos.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={downloadAllPhotos}
              data-testid="download-all-photos-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          
          {/* Upload */}
          {!readOnly && photos.length < maxPhotos && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                data-testid="upload-photo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Add Photo
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Before/After Comparison View */}
      {showCompareView && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-sm text-gray-600 flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Before & After Comparison
          </h4>
          {getComparisonPairs().length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Add "Before" and "After" photos to see comparisons
            </p>
          ) : (
            <div className="space-y-6">
              {getComparisonPairs().map((pair, i) => (
                <div key={i} className="grid grid-cols-2 gap-4">
                  {/* Before */}
                  <div className="space-y-2">
                    <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", photoTypeColors.before)}>
                      Before
                    </span>
                    {pair.before ? (
                      <div 
                        className="aspect-video bg-gray-200 rounded-lg overflow-hidden cursor-pointer"
                        onClick={() => setSelectedPhoto(pair.before)}
                      >
                        <img 
                          src={getPhotoUrl(pair.before)} 
                          alt="Before" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed">
                        <span className="text-sm text-gray-400">No before photo</span>
                      </div>
                    )}
                  </div>
                  
                  {/* After */}
                  <div className="space-y-2">
                    <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", photoTypeColors.after)}>
                      After
                    </span>
                    {pair.after ? (
                      <div 
                        className="aspect-video bg-gray-200 rounded-lg overflow-hidden cursor-pointer"
                        onClick={() => setSelectedPhoto(pair.after)}
                      >
                        <img 
                          src={getPhotoUrl(pair.after)} 
                          alt="After" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed">
                        <span className="text-sm text-gray-400">No after photo</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Photo Grid */}
      {photos.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
              onClick={() => setSelectedPhoto(photo)}
              data-testid={`photo-${photo.id}`}
            >
              <img
                src={getPhotoUrl(photo)}
                alt={photo.caption || 'Photo'}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              
              {/* Type Badge */}
              {photo.photo_type && photo.photo_type !== 'general' && (
                <span className={cn(
                  "absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium",
                  photoTypeColors[photo.photo_type]
                )}>
                  {photoTypeLabels[photo.photo_type]}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed rounded-lg p-8 text-center">
          <Image className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">No photos yet</p>
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload First Photo
            </Button>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Photo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {pendingFile && (
              <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={URL.createObjectURL(pendingFile)}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            <div>
              <Label>Photo Type</Label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="input-default w-full mt-1"
              >
                <option value="general">General</option>
                <option value="before">Before</option>
                <option value="after">After</option>
                <option value="progress">In Progress</option>
              </select>
            </div>
            
            <div>
              <Label>Caption (optional)</Label>
              <Input
                value={uploadCaption}
                onChange={(e) => setUploadCaption(e.target.value)}
                placeholder="Add a description..."
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowUploadDialog(false);
                  setPendingFile(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="btn-primary"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {selectedPhoto && (
            <div className="relative">
              <img
                src={getPhotoUrl(selectedPhoto)}
                alt={selectedPhoto.caption || 'Photo'}
                className="w-full max-h-[80vh] object-contain bg-black"
              />
              
              {/* Info Bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <div className="flex items-end justify-between text-white">
                  <div>
                    {selectedPhoto.photo_type && selectedPhoto.photo_type !== 'general' && (
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-xs font-medium mb-2 inline-block",
                        photoTypeColors[selectedPhoto.photo_type]
                      )}>
                        {photoTypeLabels[selectedPhoto.photo_type]}
                      </span>
                    )}
                    {selectedPhoto.caption && (
                      <p className="text-sm">{selectedPhoto.caption}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => downloadPhoto(selectedPhoto)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    
                    {!readOnly && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(selectedPhoto.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Close button */}
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    {ConfirmDialogComponent}
    </div>
  );
};

export default PhotoGallery;
